import "./RedbookSearchModal.scss";

import cx from "classnames";
import Button from "components/Button/Button";
import ButtonIcon from "components/ButtonIcon/ButtonIcon";
import { TextInput, Toggle } from "components/inputs";
import type { ServerSortColumn } from "components/Table/Table";
import Table from "components/Table/Table";
import { commonColumnProps, PAGE_SIZE } from "constants/TableConstants";
import debounce from "lodash/debounce";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import {
  useSearchRedbookDrugsQuery,
  useSearchRedbookUniqueProductNamesQuery,
} from "services/codes-api";

import type { RedbookDrugProduct } from "types/DataTypes";

type RedbookSearchFormData = {
  search: string;
  statusFilter: "active" | "deactivated" | "all";
  excludeRepackagers: boolean;
};

type RedbookSearchModalProps = {
  isOpen: boolean;
  onClose: () => void;
  onSelect?: (drug: RedbookDrugProduct) => void;
};

const RedbookSearchModal = ({
  isOpen,
  onClose,
  onSelect,
}: RedbookSearchModalProps) => {
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [selectedProductName, setSelectedProductName] = useState<string | null>(
    null,
  );
  const [page, setPage] = useState<number>(1);
  const [sortColumns, setSortColumns] = useState<readonly ServerSortColumn[]>(
    [],
  );
  // Filter states with defaults as specified
  const [statusFilter, setStatusFilter] = useState<
    "active" | "deactivated" | "all"
  >("active");
  const [excludeRepackagers, setExcludeRepackagers] = useState<boolean>(true);
  const [formFilter, setFormFilter] = useState<string>("");
  const [dosageFilter, setDosageFilter] = useState<string>("");

  const {
    control,
    setValue,
    formState: { errors },
  } = useForm<RedbookSearchFormData>({
    defaultValues: {
      search: "",
      statusFilter: "active",
      excludeRepackagers: true,
    },
  });

  // convert sort columns to ordering parameter format
  const ordering = useMemo(() => {
    if (sortColumns.length === 0) return undefined;
    return sortColumns
      .map(({ columnKey, direction }) => {
        const prefix = direction === "DESC" ? "-" : "";
        return `${prefix}${columnKey}`;
      })
      .join(",");
  }, [sortColumns]);

  const normalizedDosageFilter = useMemo(
    () => dosageFilter.trim(),
    [dosageFilter],
  );

  const { data: uniqueNameResults, isFetching: isFetchingUniqueNames } =
    useSearchRedbookUniqueProductNamesQuery(
      {
        search: searchTerm,
        status: statusFilter,
        exclude_repackagers: excludeRepackagers ? "true" : "false",
      },
      {
        skip: !isOpen || searchTerm.trim().length < 1 || !!selectedProductName,
      },
    );

  const uniqueProductNames = useMemo(
    () =>
      searchTerm.trim().length > 0 && !selectedProductName
        ? (uniqueNameResults?.results ?? [])
        : [],
    [searchTerm, selectedProductName, uniqueNameResults?.results],
  );

  const { data: formOptionsResults } = useSearchRedbookDrugsQuery(
    {
      search: selectedProductName || "",
      limit: 500,
      offset: 0,
      status: statusFilter,
      exclude_repackagers: excludeRepackagers ? "true" : "false",
    },
    {
      skip: !isOpen || !selectedProductName,
    },
  );

  const { data: searchResults, isFetching } = useSearchRedbookDrugsQuery(
    {
      search: selectedProductName || "",
      limit: PAGE_SIZE,
      offset: (page - 1) * PAGE_SIZE,
      ordering,
      status: statusFilter,
      exclude_repackagers: excludeRepackagers ? "true" : "false",
      form: formFilter || undefined,
      dosage: normalizedDosageFilter || undefined,
    },
    {
      skip: !isOpen || !selectedProductName,
    },
  );

  const results = useMemo(
    () => (searchResults?.results as RedbookDrugProduct[]) || [],
    [searchResults?.results],
  );

  const formQueryResults = useMemo(
    () => (formOptionsResults?.results as RedbookDrugProduct[]) || [],
    [formOptionsResults?.results],
  );

  const formOptions = useMemo(() => {
    const optionSourceResults = [...formQueryResults, ...results];
    const uniqueForms = new Set(
      optionSourceResults
        .map((result) => result.form_code?.trim())
        .filter((formCode): formCode is string => Boolean(formCode)),
    );

    if (formFilter) {
      uniqueForms.add(formFilter);
    }

    return Array.from(uniqueForms).sort((a, b) => a.localeCompare(b));
  }, [formQueryResults, results, formFilter]);

  const formSelectOptions = useMemo(() => ["", ...formOptions], [formOptions]);

  const debouncedSetSearchTerm = useMemo(
    () =>
      debounce((value: string) => {
        setSearchTerm(value);
        setPage(1); // reset to first page when search changes
      }, 500),
    [],
  );

  useEffect(
    () => () => {
      debouncedSetSearchTerm.cancel();
    },
    [debouncedSetSearchTerm],
  );

  const handleSearchChange = useCallback(
    (value: string) => {
      debouncedSetSearchTerm(value);
    },
    [debouncedSetSearchTerm],
  );

  const handlePageChange = useCallback((newPage: number) => {
    setPage(newPage);
  }, []);

  const handleSortChange = useCallback(
    (newSortColumns: readonly ServerSortColumn[]) => {
      setSortColumns(newSortColumns);
      setPage(1); // reset to first page when sort changes
    },
    [],
  );

  const columns = useMemo(
    () => [
      {
        key: "ndc",
        name: "NDC",
        ...commonColumnProps,
        minWidth: 120,
      },
      {
        key: "product_name",
        name: "Product Name",
        ...commonColumnProps,
        minWidth: 250,
      },
      {
        key: "active_ingredient",
        name: "Active Ingredient",
        ...commonColumnProps,
        minWidth: 150,
      },
      {
        key: "dea_class_code",
        name: "DEA Class",
        ...commonColumnProps,
        minWidth: 100,
      },
      {
        key: "product_category",
        name: "Category",
        ...commonColumnProps,
        minWidth: 100,
      },
      {
        key: "dosage",
        name: "Dosage",
        ...commonColumnProps,
        minWidth: 100,
      },
      {
        key: "is_generic",
        name: "Generic",
        ...commonColumnProps,
        minWidth: 90,
        renderCell: ({ row }: { row: RedbookDrugProduct }) =>
          row.is_generic === true ? "Yes" : "No",
      },
      {
        key: "is_repackager",
        name: "Repkg",
        ...commonColumnProps,
        minWidth: 80,
        renderCell: ({ row }: { row: RedbookDrugProduct }) =>
          row.is_repackager === true ? "Y" : "N",
      },
      {
        key: "form_code",
        name: "Form",
        ...commonColumnProps,
        minWidth: 90,
      },
      {
        key: "awp_p_price",
        name: "AWP Pkg Price",
        ...commonColumnProps,
        minWidth: 130,
        renderCell: ({ row }: { row: RedbookDrugProduct }) =>
          row.awp_package_price ? `$${parseFloat(row.awp_package_price)}` : "-",
      },
      {
        key: "awp_u_price",
        name: "AWP Unit Price",
        ...commonColumnProps,
        minWidth: 130,
        renderCell: ({ row }: { row: RedbookDrugProduct }) =>
          row.awp_unit_price ? `$${parseFloat(row.awp_unit_price)}` : "-",
      },
    ],
    [],
  );

  const handleRowClick = useCallback(
    (row: RedbookDrugProduct) => {
      if (onSelect) {
        onSelect(row);
        onClose();
      }
    },
    [onSelect, onClose],
  );

  const handleProductNameSelect = useCallback(
    (name: string) => {
      setSelectedProductName(name);
      setSearchTerm(name);
      setValue("search", name);
      setPage(1);
      setSortColumns([]);
      setFormFilter("");
      setDosageFilter("");
    },
    [setValue],
  );

  const handleChangeDrug = useCallback(() => {
    setSelectedProductName(null);
    setSearchTerm("");
    setValue("search", "");
    setPage(1);
    setSortColumns([]);
    setFormFilter("");
    setDosageFilter("");
  }, [setValue]);

  return (
    <div className={cx("overlay overlay-slidedown", { open: isOpen })}>
      <div className="grid-layout gl-1 modal-grid-wrapper">
        <div className="data-block no-hover">
          <div className="data-block-header flex ai-c jc-sb">
            Redbook Search
            <ButtonIcon kind="neutral" onClick={onClose}>
              close
            </ButtonIcon>
          </div>

          <div className="redbook-search-modal">
            <Controller
              name="search"
              control={control}
              rules={{
                required: "Search term is required",
                minLength: {
                  value: 3,
                  message: "Search term must be at least 3 characters",
                },
              }}
              render={({ field }) => (
                <div className="redbook-search-input-wrapper">
                  <TextInput
                    {...field}
                    data-testid="search-input"
                    classNames={cx({
                      "redbook-search-input--clearable": !!selectedProductName,
                    })}
                    label={
                      selectedProductName
                        ? "Selected Drug Name"
                        : "Find Drug Name"
                    }
                    placeholder={
                      selectedProductName
                        ? "Click X to clear and choose a different drug"
                        : "Enter a drug name or active ingredient to find matching Redbook products"
                    }
                    errors={errors}
                    disabled={!!selectedProductName}
                    onChange={(e) => {
                      field.onChange(e);
                      handleSearchChange(e.target.value);
                    }}
                  />
                  {selectedProductName && (
                    <button
                      type="button"
                      className="redbook-search-input-clear material-icons"
                      aria-label="Clear selected drug"
                      data-testid="clear-selected-drug-button"
                      onClick={handleChangeDrug}
                    >
                      close
                    </button>
                  )}
                </div>
              )}
            />

            {selectedProductName ? (
              <>
                <div className="redbook-search-filters">
                  <div className="filter-group">
                    <label className="filter-label">Status:</label>
                    <div className="filter-buttons">
                      {(["active", "deactivated", "all"] as const).map(
                        (status) => (
                          <Button
                            key={status}
                            className={cx({
                              "allan-koba": statusFilter === status,
                            })}
                            onClick={() => {
                              setStatusFilter(status);
                              setPage(1); // reset to first page when filter changes
                            }}
                          >
                            {status.charAt(0).toUpperCase() + status.slice(1)}
                          </Button>
                        ),
                      )}
                    </div>
                  </div>

                  <div className="filter-group">
                    <Toggle
                      rightLabel="Exclude Repackagers"
                      checked={excludeRepackagers}
                      onChange={(checked) => {
                        setExcludeRepackagers(checked);
                        setPage(1); // reset to first page when filter changes
                      }}
                    />
                  </div>

                  <div className="filter-group">
                    <label
                      className="filter-label"
                      htmlFor="redbook-form-filter"
                    >
                      Form:
                    </label>
                    <div className="redbook-form-native-select">
                      <select
                        id="redbook-form-filter"
                        data-testid="form-filter-select"
                        value={formFilter}
                        onChange={(event) => {
                          setFormFilter(event.target.value);
                          setPage(1);
                        }}
                      >
                        {formSelectOptions.map((formCode) => (
                          <option
                            key={formCode || "all"}
                            value={formCode}
                            data-testid={`form-option-${formCode || "all"}`}
                          >
                            {formCode || "All"}
                          </option>
                        ))}
                      </select>
                      <span className="material-icons">
                        keyboard_arrow_down
                      </span>
                    </div>
                  </div>

                  <div className="filter-group">
                    <label
                      className="filter-label"
                      htmlFor="redbook-dosage-filter-input"
                    >
                      Dosage:
                    </label>
                    <TextInput
                      id="redbook-dosage-filter-input"
                      name="redbook-dosage-filter-input"
                      data-testid="dosage-filter-input"
                      value={dosageFilter}
                      classNames="redbook-dosage-input"
                      onChange={(event) => {
                        setDosageFilter(event.target.value);
                        setPage(1);
                      }}
                      placeholder="Enter dosage (e.g. 50, 50 mg, 0.5%)"
                    />
                  </div>
                </div>

                <div className="search-results">
                  <Table<RedbookDrugProduct>
                    columns={columns}
                    rows={isFetching ? [] : results}
                    rowKeyGetter={(row) => row.id}
                    onCellClick={({ row }) => handleRowClick(row)}
                    className="redbook-results-table"
                    isLoading={isFetching}
                    useServerSorting={true}
                    serverSorting={{
                      sortColumns,
                      onSortChange: handleSortChange,
                    }}
                    pagination={{
                      pageSize: PAGE_SIZE,
                      pageIndex: page,
                      rowCount: searchResults?.count || 0,
                      onPageChange: handlePageChange,
                      isLoading: isFetching,
                    }}
                    zebraStripes={true}
                    customScrollbars={true}
                    emptyTableMessage={
                      isFetching ? "Searching..." : "No results found"
                    }
                  />
                </div>
              </>
            ) : (
              <div className="search-results">
                <Table<{ id: string; product_name: string }>
                  columns={[
                    {
                      key: "product_name",
                      name: "Drug Name",
                      ...commonColumnProps,
                      minWidth: 350,
                    },
                  ]}
                  rows={uniqueProductNames.map((name) => ({
                    id: name,
                    product_name: name,
                  }))}
                  rowKeyGetter={(row) => row.id}
                  onCellClick={({ row }) =>
                    handleProductNameSelect(row.product_name)
                  }
                  className="redbook-results-table"
                  isLoading={isFetchingUniqueNames}
                  zebraStripes={true}
                  customScrollbars={true}
                  emptyTableMessage={
                    searchTerm.length < 1
                      ? "Enter a drug name or active ingredient to find matching Redbook products"
                      : isFetchingUniqueNames
                        ? "Searching drug names..."
                        : "No drug names found"
                  }
                />
              </div>
            )}
          </div>

          <div className="flex jc-c ai-c mt-1">
            <Button onClick={onClose}>Close</Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RedbookSearchModal;
